import {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import Typography from "@mui/material/Typography";
import {
  confirmPurchaseOrderImport,
  getAllBranch,
  previewPurchaseOrderImport,
} from "../../services";
import { Branch } from "../../types/user.type";
import {
  PurchaseOrderChain,
  PurchaseOrderImportBranchChoice,
  PurchaseOrderImportConfirmData,
  PurchaseOrderImportPreviewData,
  PurchaseOrderImportReportRow,
} from "../../types/purchase-order.type";
// ด่านตรวจไฟล์ฝั่ง browser ของแผนวิ่ง — กติกาตรงกันทุกข้อ (.xlsx/.xls, ไม่เกิน 10MB,
// ต้องเป็นไฟล์ Excel จริงไม่ใช่ไฟล์ที่ถูกเปลี่ยนนามสกุลมา) จึงใช้ตัวเดียวกัน
// การเขียนชุดที่สองมีแต่จะทำให้สองจอบล็อกไฟล์คนละแบบเมื่อกฎเปลี่ยน
import { checkPathwayFile } from "../PathwayUpload/fileCheck";
import { resolveImportPreviewFailure } from "./uploadError";
import { DEFAULT_CHAIN } from "./constants";
import {
  applyBranchChoice,
  BranchChoiceMap,
  buildBranchRequiredStoreIndex,
  countSavedRows,
  isPastImportable,
  pastDeliverySkippedReportRows,
} from "./branchChoice";
import SelectFileCard from "./SelectFileCard";
import PreviewStep from "./PreviewStep";
import { AutoFillNote } from "./PreviewRowsTable";
import DoneStep from "./DoneStep";

const STEP_SELECT_FILE = 0;
const STEP_PREVIEW = 1;
const STEP_DONE = 2;
const STEP_LABELS = ["เลือกไฟล์", "ตรวจ + ตัวอย่าง", "จบ"];

const EMPTY_RESPONSE_MESSAGE =
  "ระบบไม่ได้ส่งผลตรวจกลับมา กรุณาลองตรวจไฟล์อีกครั้ง";

const resolveActiveStep = (
  preview: PurchaseOrderImportPreviewData | null,
  confirmResult: PurchaseOrderImportConfirmData | null,
): number => {
  if (confirmResult) return STEP_DONE;
  if (preview) return STEP_PREVIEW;
  return STEP_SELECT_FILE;
};

/**
 * W2 · นำเข้าไฟล์ PO (HO)
 *
 *   1. เลือกไฟล์   เลือกเชน + ไฟล์ แล้วสั่งตรวจ (preview) ยังไม่เขียนฐานข้อมูล
 *   2. ตรวจ + ตัวอย่าง  ผลตรวจ 4 ค่า + ทุกแถวพร้อมผลตรวจ · เลือกสาขาให้แถวที่ระบบ
 *                       ระบุสาขาไม่ได้ (ทีละแถวหรือหลายแถว และเติมให้ร้านเดียวกันเอง)
 *                       แถวที่ยังไม่เลือกบันทึกได้เป็น ยังไม่ระบุสาขา · ยืนยันแล้วระบบ
 *                       เขียนจากแผนที่พักไว้ ไม่อ่านไฟล์ซ้ำ
 *   3. จบ          สรุปที่บันทึก แล้วส่งให้สาขาต่อได้จากจอเดียวกัน
 *
 * ผลตรวจพักไว้ 30 นาทีและใช้ได้ครั้งเดียว หมดอายุแล้วต้องเลือกไฟล์แล้วตรวจใหม่
 */
export default function PurchaseOrderImport() {
  const [chain, setChain] = useState<PurchaseOrderChain>(DEFAULT_CHAIN);
  const [file, setFile] = useState<File | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [applying, setApplying] = useState(false);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [preview, setPreview] = useState<PurchaseOrderImportPreviewData | null>(
    null,
  );
  const [branchChoiceByRow, setBranchChoiceByRow] = useState<BranchChoiceMap>(
    {},
  );
  const [autoFillNote, setAutoFillNote] = useState<AutoFillNote | null>(null);
  // แถวเกินกำหนดส่งที่ HO ติ๊ก "นำเข้า" · ค่าตั้งต้นว่าง = ไม่นำเข้าสักแถว
  const [includedPastRowNos, setIncludedPastRowNos] = useState<Set<number>>(
    () => new Set(),
  );
  const [confirmResult, setConfirmResult] =
    useState<PurchaseOrderImportConfirmData | null>(null);
  // แถวที่ข้าม (อ่านไม่ได้ + เกินกำหนดส่งที่ไม่ได้นำเข้า) เก็บไว้ให้ขั้นจบดาวน์โหลดเป็น CSV
  // เพราะผลตรวจถูกล้างทิ้งทันทีที่ยืนยัน
  const [skippedReport, setSkippedReport] = useState<
    PurchaseOrderImportReportRow[]
  >([]);

  const loadBranches = useCallback(async () => {
    try {
      const {
        data: { data },
      } = await getAllBranch();
      setBranches(data ?? []);
    } catch {
      // interceptor แสดง error ให้แล้ว · แถวที่ backend ส่งสาขา candidate มาให้
      // ยังเลือกได้ตามปกติ เพราะตัวเลือกกลุ่มนั้นมากับผลตรวจ ไม่ได้มาจากรายการนี้
    }
  }, []);

  useEffect(() => {
    loadBranches();
  }, [loadBranches]);

  const resetPreview = useCallback(() => {
    setPreview(null);
    setBranchChoiceByRow({});
    setAutoFillNote(null);
    setIncludedPastRowNos(new Set());
    setSkippedReport([]);
    setConfirmResult(null);
  }, []);

  // ดัชนี store number ของแถว "ต้องเลือกสาขา" ที่จะถูกบันทึก · สร้างใหม่เมื่อผลตรวจหรือ
  // ชุดแถวเกินกำหนดส่งที่ติ๊กนำเข้าเปลี่ยน (ไม่ใช่ทุกครั้งที่เลือกสาขา)
  const storeIndex = useMemo(
    () => buildBranchRequiredStoreIndex(preview?.rows ?? [], includedPastRowNos),
    [preview, includedPastRowNos],
  );

  const handleTogglePastRow = useCallback((rowNo: number) => {
    setIncludedPastRowNos((current) => {
      const next = new Set(current);
      if (next.has(rowNo)) next.delete(rowNo);
      else next.add(rowNo);
      return next;
    });
  }, []);

  const handleSetPastRows = useCallback(
    (rowNos: number[], include: boolean) => {
      setIncludedPastRowNos((current) => {
        const next = new Set(current);
        rowNos.forEach((rowNo) => {
          if (include) next.add(rowNo);
          else next.delete(rowNo);
        });
        return next;
      });
    },
    [],
  );

  const handleSetAllPastRows = useCallback(
    (include: boolean) => {
      if (!include || !preview) {
        setIncludedPastRowNos(new Set());
        return;
      }
      setIncludedPastRowNos(
        new Set(
          preview.rows.filter(isPastImportable).map((row) => row.row_no),
        ),
      );
    },
    [preview],
  );

  const handleChangeChain = (nextChain: PurchaseOrderChain) => {
    setChain(nextChain);
    // ผลตรวจผูกกับเชนที่ใช้ตรวจ เปลี่ยนเชนแล้วต้องตรวจใหม่
    resetPreview();
    setFailure(null);
  };

  const handleSelectFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0] ?? null;
    // อนุญาตให้เลือกไฟล์เดิมซ้ำได้ (onChange ไม่ fire ถ้าค่าเดิม)
    event.target.value = "";
    resetPreview();
    setFailure(null);

    if (!selected) {
      setFile(null);
      return;
    }

    const problem = await checkPathwayFile(selected);
    if (problem !== null) {
      setFile(null);
      setFailure(problem);
      return;
    }

    setFile(selected);
  };

  const handleCheck = async () => {
    if (!file) return;

    setChecking(true);
    setFailure(null);
    try {
      const {
        data: { data },
      } = await previewPurchaseOrderImport(file, chain);

      if (!data) {
        resetPreview();
        setFailure(EMPTY_RESPONSE_MESSAGE);
        return;
      }

      setBranchChoiceByRow({});
      setIncludedPastRowNos(new Set());
      setPreview(data);
    } catch (error) {
      // previewPurchaseOrderImport ปิด global toast ไว้ — เหตุผลต้องอยู่คู่กับปุ่มเลือกไฟล์
      resetPreview();
      setFailure(resolveImportPreviewFailure(error));
    } finally {
      setChecking(false);
    }
  };

  // ค่าล่าสุดของ state ที่ applyChoice อ่าน · เก็บใน ref เพื่อให้ callback คงที่ข้าม render
  // ไม่งั้นทุกการเลือกสาขาสร้าง callback ใหม่ และแถวที่ห่อ memo ต้อง render ใหม่ทั้งหน้า
  const choicesRef = useRef<BranchChoiceMap>(branchChoiceByRow);
  choicesRef.current = branchChoiceByRow;
  const storeIndexRef = useRef(storeIndex);
  storeIndexRef.current = storeIndex;

  /**
   * เลือกสาขาให้แถวที่สั่ง แล้วเติมให้แถวร้านเดียวกันที่ยังว่าง (ไม่ทับที่เลือกไว้แล้ว)
   * · อ่านค่าปัจจุบันจาก ref ไม่ใช่ใน updater เพราะต้องรู้จำนวนที่เติมเพื่อแจ้งผู้ใช้
   * และ updater ต้องไม่มี side effect
   */
  const applyChoice = useCallback((rowNos: number[], companyId: string) => {
    const { choices, autoFilledCount } = applyBranchChoice(
      choicesRef.current,
      rowNos,
      companyId,
      storeIndexRef.current,
    );
    choicesRef.current = choices;
    setBranchChoiceByRow(choices);
    if (autoFilledCount > 0) {
      setAutoFillNote((previous) => ({
        count: autoFilledCount,
        seq: (previous?.seq ?? 0) + 1,
      }));
    }
  }, []);

  const handleChooseBranch = useCallback(
    (rowNo: number, companyId: string) => {
      applyChoice([rowNo], companyId);
    },
    [applyChoice],
  );

  const handleBulkChooseBranch = useCallback(
    (rowNos: number[], companyId: string) => {
      applyChoice(rowNos, companyId);
    },
    [applyChoice],
  );

  const handleDismissAutoFillNote = useCallback(() => {
    setAutoFillNote(null);
  }, []);

  /**
   * ส่งเฉพาะแถว "ต้องเลือกสาขา" ที่จะถูกบันทึกและเลือกสาขาแล้ว — backend ปฏิเสธ 422
   * ถ้ามี choice ชี้ไปที่แถวที่ระบบจับคู่ร้านได้เอง · แถวในดัชนีคือแถวที่ต้องมีสาขาจริง
   * (ไม่รวมแถวเกินกำหนดส่งที่ไม่ได้ติ๊กนำเข้า)
   */
  const branchChoices = useMemo<PurchaseOrderImportBranchChoice[]>(() => {
    const choices: PurchaseOrderImportBranchChoice[] = [];
    storeIndex.storeByRowNo.forEach((_store, rowNo) => {
      const companyId = branchChoiceByRow[rowNo];
      if (companyId !== undefined) {
        choices.push({ row_no: rowNo, company_id: companyId });
      }
    });
    return choices;
  }, [storeIndex, branchChoiceByRow]);

  // นับจาก rows ชุดเดียวกับที่ตารางแสดง ไม่ใช่จาก summary ตัวเลขที่บอกผู้ใช้จึงตรงกับ
  // dropdown และช่องติ๊กนำเข้าที่เขาเห็นเสมอ
  const unchosenBranchCount =
    storeIndex.storeByRowNo.size - branchChoices.length;

  const savedCount = useMemo(
    () => (preview ? countSavedRows(preview.rows, includedPastRowNos) : 0),
    [preview, includedPastRowNos],
  );

  const handleConfirm = async () => {
    if (!preview) return;

    setApplying(true);
    try {
      const {
        data: { data },
      } = await confirmPurchaseOrderImport(
        preview.upload_id,
        branchChoices,
        Array.from(includedPastRowNos),
      );

      // ผลตรวจใช้ได้ครั้งเดียวไม่ว่าจะตอบอะไรกลับมา จึงล้างทิ้งทั้งสองทาง — ทางที่
      // ตอบว่างพากลับไปขั้นเลือกไฟล์ซึ่งเป็นที่เดียวที่แสดงเหตุผลให้อ่านได้
      if (!data) {
        resetPreview();
        setFailure(EMPTY_RESPONSE_MESSAGE);
        return;
      }

      // ขั้นที่ 3 สรุปผลที่บันทึกให้ครบอยู่แล้ว จึงไม่ต้องมี toast ซ้ำอีกชั้น
      setSkippedReport([
        ...preview.skipped,
        ...pastDeliverySkippedReportRows(preview.rows, includedPastRowNos),
      ]);
      setPreview(null);
      setBranchChoiceByRow({});
      setAutoFillNote(null);
      setIncludedPastRowNos(new Set());
      setFile(null);
      setConfirmResult(data);
    } catch {
      // interceptor แสดง error ให้แล้ว (403 ผลตรวจของผู้ใช้อื่น, 404 หมดอายุ)
      // — ผู้ใช้แก้แล้วกดยืนยันซ้ำได้
    } finally {
      setApplying(false);
    }
  };

  const handleRestart = () => {
    setFile(null);
    setFailure(null);
    resetPreview();
  };

  const activeStep = resolveActiveStep(preview, confirmResult);

  return (
    <Box sx={{ padding: "8px 0" }}>
      <Box mb={2}>
        <Typography variant="h6" fontWeight={700}>
          นำเข้าไฟล์ PO
        </Typography>
        <Typography variant="body2" color="text.secondary">
          นำเข้าเลข PO จากไฟล์ของเชน แล้วส่งให้สาขาใช้งาน
        </Typography>
      </Box>

      <Stack gap={2}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {STEP_LABELS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* คงการ์ดเลือกไฟล์ไว้ระหว่างดูผลตรวจด้วย เพื่อให้เปลี่ยนไฟล์หรือเชนแล้วตรวจใหม่ได้
            โดยไม่ต้องยืนยันก่อน — การเลือกใหม่ล้างผลตรวจเดิมให้เองอยู่แล้ว */}
        {!confirmResult && (
          <SelectFileCard
            chain={chain}
            fileName={file?.name ?? null}
            checking={checking}
            failure={failure}
            onChangeChain={handleChangeChain}
            onSelectFile={handleSelectFile}
            onCheck={handleCheck}
          />
        )}

        {preview && (
          <PreviewStep
            key={preview.upload_id}
            preview={preview}
            branches={branches}
            branchChoiceByRow={branchChoiceByRow}
            unchosenBranchCount={unchosenBranchCount}
            savedCount={savedCount}
            includedPastRowNos={includedPastRowNos}
            onTogglePastRow={handleTogglePastRow}
            onSetAllPastRows={handleSetAllPastRows}
            onSetPastRows={handleSetPastRows}
            applying={applying}
            onChooseBranch={handleChooseBranch}
            onBulkChooseBranch={handleBulkChooseBranch}
            autoFillNote={autoFillNote}
            onDismissAutoFillNote={handleDismissAutoFillNote}
            onConfirm={handleConfirm}
          />
        )}

        {confirmResult && (
          <DoneStep
            result={confirmResult}
            skippedReport={skippedReport}
            onRestart={handleRestart}
          />
        )}
      </Stack>
    </Box>
  );
}
